/**
 * Subscription-related TypeScript interfaces and types.
 * 
 * Defines the shape of subscription data for type safety throughout the frontend.
 */

export interface PlanFeatures {
  simulationsPerMonth: number;
  allowedUseCases: string[] | 'all';
  maxPersonasPerSimulation: number;
  maxSegments: number;
  maxAudiences: number;
  prioritySupport: boolean;
  apiAccess?: boolean;
  customBranding?: boolean;
  dedicatedAccountManager?: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description?: string;
  isPopular?: boolean;
  features: PlanFeatures;
}

export interface UsageInfo {
  // Monthly refreshable counters
  simulationsUsed: number;
  simulationsLimit: number;
  questionsUsed: number; // Total questions parsed this month
  groupChatsUsed: number;
  groupChatsLimit: number;
  simulationChatsUsed: number;
  simulationChatsLimit: number;
  
  // Static validation limits (not counters)
  questionsPerSurvey: number; // e.g., 4 - max questions per individual survey
  optionsPerQuestion: number; // e.g., 5 - max options per individual question
  
  // Legacy fields for compatibility
  usagePercentage: number;
  remainingSimulations: number;
  daysUntilReset: number;
  useCaseBreakdown: Record<string, number>;
  periodStart: string;
  periodEnd: string;
}

export interface CurrentSubscription {
  id: string;
  name: string;
  price: number;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing' | 'expired' | 'pending';
  startDate?: string;
  endDate?: string;
  cancelAtPeriodEnd: boolean;
  usage: {
    simulationsUsed: number;
    simulationsLimit: number;
    resetDate?: string;
  };
}

export interface FeatureAccess {
  currentPlan: string;
  planFeatures: {
    simulationsPerMonth: number;
    maxPersonasPerSimulation: number;
    maxSegments: number;
    maxAudiences: number;
    prioritySupport: boolean;
    apiAccess: boolean;
    customBranding: boolean;
  };
  useCases: Record<string, {
    displayName: string;
    description: string;
    category: string;
    hasAccess: boolean;
    minPlanRequired: string;
  }>;
  isAdmin: boolean;
}

export interface CheckoutSession {
  checkoutUrl: string;
  sessionId: string;
}

export interface AccessCheck {
  hasAccess: boolean;
  reason: string;
  currentPlan?: string;
  requiredPlan?: string;
  allowedUseCases?: string[];
}

export interface BillingPortalSession {
  portalUrl: string;
}

export interface SubscriptionError {
  success: false;
  error: string;
  details?: any;
}

export interface SubscriptionSuccess<T = any> {
  success: true;
  data?: T;
  message?: string;
}

export type SubscriptionResponse<T = any> = SubscriptionSuccess<T> | SubscriptionError;

// Plan tier constants
export const PLAN_TIERS = {
  FREE: 'free',
  STARTER: 'starter', 
  PROFESSIONAL: 'professional',
  BUSINESS: 'business',
  ENTERPRISE: 'enterprise'
} as const;

export type PlanTier = typeof PLAN_TIERS[keyof typeof PLAN_TIERS];

// Subscription status constants
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  PAST_DUE: 'past_due',
  TRIALING: 'trialing',
  EXPIRED: 'expired',
  PENDING: 'pending'
} as const;

export type SubscriptionStatus = typeof SUBSCRIPTION_STATUS[keyof typeof SUBSCRIPTION_STATUS];

// Use case categories
export const USE_CASE_CATEGORIES = {
  RESEARCH: 'research',
  TESTING: 'testing',
  CREATIVE: 'creative',
  STRATEGY: 'strategy',
  MARKETPLACE: 'marketplace',
  ADMIN: 'admin'
} as const;

export type UseCaseCategory = typeof USE_CASE_CATEGORIES[keyof typeof USE_CASE_CATEGORIES];